import React, { useCallback, useEffect, useState } from "react";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import "./LinkedAccounts.scss";
import {
  ApplicationResponse,
  applicationStatuses,
} from "@quest-finance/quest-fe-shared/dist/application/";
import { dateFormat } from "@quest-finance/quest-fe-shared/dist/common/utils/date";
import { replaceAll } from "@quest-finance/quest-fe-shared/dist/common/utils/string";
import { Spin } from "antd";
import cs from "classnames";
import { isValid } from "date-fns";
import lodash from "lodash";
import { NumericFormat } from "react-number-format";
import { useSelector } from "react-redux";
import { Link } from "react-router";
import { showErrorPopUp } from "../../../../common/components/ShowNotification/showNotification";
import { LONG_DATE_FORMAT } from "../../../../common/contstants/app";
import { processError } from "../../../../common/utils/error";
import { assessmentResponseSelector } from "../../../selectors/assessmentDetail";
import AssessmentService from "../../../services/AssessmentService";
import { AssessmentDetail } from "../../../types/AssessmentDetail";
import { AssessmentTypes } from "../../../types/AssessmentType";

const LinkedAccounts: React.FC = () => {
  const assessmentDetail = useSelector(assessmentResponseSelector);
  const abn = lodash.get(assessmentDetail, "data.applicant.abn", "");
  const [assessments, setAssessments] = useState<AssessmentDetail[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const getAssessments = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await AssessmentService.getLinkedAccountsList(abn, {
        limit: 14,
        type: AssessmentTypes.APPLICATION,
      });

      setAssessments(data.filter(({ id }) => assessmentDetail.id !== id));
    } catch (e) {
      processError(e, (errorMessage) => {
        showErrorPopUp(errorMessage);
      });
    } finally {
      setIsLoading(false);
    }
  }, [abn, assessmentDetail]);

  useEffect(() => {
    if (abn) getAssessments();
  }, [getAssessments, abn]);

  if (isLoading || !assessmentDetail.id) {
    return <Spin data-testid="linked-accounts-loading" />;
  }

  const header = assessments.length ? (
    <>
      <ExclamationCircleOutlined className="icon" />
      Linked accounts found
    </>
  ) : (
    "No linked accounts found"
  );

  return (
    <div className="linked-accounts">
      <div className="header">{header}</div>
      <div className="linked-accounts-wrapper">
        <table className="linked-accounts-table">
          <thead>
            <tr>
              <th className="id-column">Loan ID</th>
              <th className="status-column">Status</th>
              <th className="date-column">Status Date</th>
              <th className="amount-column">Amount Funded</th>
            </tr>
          </thead>
          <tbody>
            {[assessmentDetail, ...assessments].map((assessment, i) => {
              const { id, externalId, status } = assessment;
              const data = assessment.data as unknown as ApplicationResponse;
              const applicationNumber = lodash.get(
                data,
                "applicationNumber",
                ""
              );
              const rawDate =
                assessment.settledAt || lodash.get(data, "submittedAt", null);
              const statusDate = rawDate ? new Date(rawDate as Date) : null;
              const finPowerLink =
                process.env.REACT_APP_FINPOWER_LINK && applicationNumber
                  ? replaceAll(
                      process.env.REACT_APP_FINPOWER_LINK ?? "",
                      "[APPLICATION_NUMBER]",
                      applicationNumber
                    )
                  : "";

              const amountFunded =
                lodash.get(data, "quote.amountFinanced", 0) +
                lodash.get(data, "quote.brokerageNominalWithGst", 0);

              const amountColumn = (
                <NumericFormat
                  thousandSeparator
                  prefix={"$"}
                  value={amountFunded}
                  displayType="text"
                  decimalScale={2}
                  fixedDecimalScale
                />
              );

              return (
                <tr
                  key={id}
                  className={cs({ current: id === assessmentDetail.id })}
                >
                  <td
                    className="id-column"
                    data-testid={`la-application-num-${i}`}
                  >
                    <Link to={`/application/applications/${externalId}/quotes`}>
                      {applicationNumber}
                    </Link>
                  </td>
                  <td
                    className={cs("status-column", status)}
                    data-testid={`la-status-${i}`}
                  >
                    {applicationStatuses.APPLICATION_STATUS_LABELS[status]}
                  </td>
                  <td className="date-column" data-testid={`la-date-${i}`}>
                    {statusDate && isValid(statusDate)
                      ? dateFormat(statusDate, LONG_DATE_FORMAT)
                      : "-"}
                  </td>
                  <td className="amount-column" data-testid={`la-amount-${i}`}>
                    {status ===
                      applicationStatuses.APPLICATION_STATUSES.SETTLED &&
                    finPowerLink ? (
                      <a
                        href={finPowerLink}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`la-finpower-link-${i}`}
                      >
                        {amountColumn}
                      </a>
                    ) : (
                      amountColumn
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LinkedAccounts;
